import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useMemo, useState } from "react";

import ChainRunOverlay from "./ChainRunOverlay";
import ContributionsTab from "./ContributionsTab";
import IngestJobsTray from "./IngestJobsTray";
import IngestTab from "./IngestTab";
import ReviewTab from "./ReviewTab";
import SearchEditTab from "./SearchEditTab";
import { setIngestJobsUserScope } from "./ingestJobs";
import { SecondaryButton } from "./shared";
import { IngestReviewItem } from "./types";
import {
  chainIdConfig,
  setAutoMergeDuplicates,
  setIngestChainIdOverride,
  setMergeChainIdOverride,
  useCatalogApi,
} from "./useCatalogApi";
import { useIngestJobs } from "./useIngestJobs";

type Mode = "add" | "manage" | "mine";
type Step = "input" | "review";

interface CatalogManagerProps {
  customerName?: string;
}

/** A numbered step pill in the "add products" flow. */
const StepPill: React.FC<{
  index: number;
  label: string;
  hint: string;
  state: "done" | "active" | "todo";
  enabled: boolean;
  onClick: () => void;
}> = ({ index, label, hint, state, enabled, onClick }) => {
  const circle =
    state === "done"
      ? "bg-green-600 text-white"
      : state === "active"
      ? "bg-indigo-600 text-white"
      : "bg-gray-200 text-gray-500";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!enabled}
      className={`flex flex-1 items-center gap-3 rounded-lg border px-4 py-3 text-left transition ${
        state === "active"
          ? "border-indigo-300 bg-indigo-50"
          : "border-gray-200 bg-white"
      } ${enabled ? "hover:border-indigo-300" : "cursor-default opacity-80"}`}
    >
      <span
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${circle}`}
      >
        {state === "done" ? "✓" : index}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-gray-900">
          {label}
        </span>
        <span className="block truncate text-xs text-gray-500">{hint}</span>
      </span>
    </button>
  );
};

const ModeButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
      active
        ? "bg-indigo-600 text-white shadow"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`}
  >
    {children}
  </button>
);

const CatalogManager: React.FC<CatalogManagerProps> = ({ customerName }) => {
  const api = useCatalogApi();
  const { user } = useAuth0();
  const jobs = useIngestJobs(api);
  const [mode, setMode] = useState<Mode>("add");
  const [step, setStep] = useState<Step>("input");
  const [reviewItems, setReviewItems] = useState<IngestReviewItem[]>([]);
  // The chain execution that produced the items currently under review; stamped
  // as the origin id on write so records link back to their run on easyai.
  const [reviewExecutionId, setReviewExecutionId] = useState<
    string | undefined
  >(undefined);
  // Which background job's progress dialog is open (null = hidden/tray-only).
  const [focusedJobId, setFocusedJobId] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Scope persisted jobs to the signed-in user so runs don't leak across
  // accounts on a shared machine.
  useEffect(() => {
    setIngestJobsUserScope(user?.sub);
  }, [user?.sub]);

  const focusedJob = useMemo(
    () => jobs.jobs.find((j) => j.id === focusedJobId) || null,
    [jobs.jobs, focusedJobId]
  );

  // Load a finished run's results into the review step.
  const openRunResults = (jobId: string) => {
    const job = jobs.getJob(jobId);
    if (!job?.resultItems) return;
    setReviewItems(job.resultItems);
    setReviewExecutionId(job.executionId);
    setFocusedJobId(null);
    setMode("add");
    setStep("review");
  };

  // When the dialog-focused run finishes, auto-open its results (completed) or
  // drop back to the tray (failed/cancelled) so the dialog never sticks open.
  useEffect(() => {
    if (!focusedJob) return;
    if (focusedJob.status === "completed" && focusedJob.resultItems) {
      openRunResults(focusedJob.id);
    } else if (
      focusedJob.status === "failed" ||
      focusedJob.status === "cancelled"
    ) {
      setFocusedJobId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedJob?.status]);

  const [cfg, setCfg] = useState(() => chainIdConfig());
  const [ingestDraft, setIngestDraft] = useState(cfg.ingest.value);
  const [mergeDraft, setMergeDraft] = useState(cfg.merge.value);
  const [autoMergeDraft, setAutoMergeDraft] = useState(cfg.autoMerge);

  const ingestChainId = cfg.ingest.value;
  const mergeChainId = cfg.merge.value;
  const autoMerge = cfg.autoMerge;

  const saveSettings = () => {
    setIngestChainIdOverride(ingestDraft);
    setMergeChainIdOverride(mergeDraft);
    setAutoMergeDuplicates(autoMergeDraft);
    setCfg(chainIdConfig());
    setSettingsOpen(false);
  };

  // Start a background research run and pop its live dialog (which is hideable).
  const submitIngest = async (foodInput: string, productCount: number) => {
    const jobId = await jobs.submit(ingestChainId, foodInput, productCount);
    setFocusedJobId(jobId);
  };

  const startNewBatch = () => {
    setReviewItems([]);
    setReviewExecutionId(undefined);
    setStep("input");
  };

  const focusedInFlight =
    !!focusedJob &&
    (focusedJob.status === "running" || focusedJob.status === "starting");

  const missingChains = !ingestChainId || !mergeChainId;
  const hasReview = reviewItems.length > 0;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Catalog Manager</h2>
          <p className="text-sm text-gray-600">
            Add new branded foods to the Passio database, or search and update
            the ones already there.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setSettingsOpen((o) => !o)}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          title="Chain settings"
        >
          ⚙ Settings
        </button>
      </div>

      {settingsOpen && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h3 className="text-sm font-semibold text-gray-800">
            Chain configuration
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            Override the EasyAI chain IDs used by this tool. Saved to this
            browser only; leave blank to use the environment default.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-600">
                Ingest pipeline chain ID
              </label>
              <input
                value={ingestDraft}
                onChange={(e) => setIngestDraft(e.target.value)}
                placeholder={cfg.ingest.envDefault || "uuid"}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-indigo-500 focus:outline-none"
              />
              <span className="text-[11px] text-gray-400">
                env default: {cfg.ingest.envDefault || "(none)"}
              </span>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">
                Compare/merge chain ID
              </label>
              <input
                value={mergeDraft}
                onChange={(e) => setMergeDraft(e.target.value)}
                placeholder={cfg.merge.envDefault || "uuid"}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-indigo-500 focus:outline-none"
              />
              <span className="text-[11px] text-gray-400">
                env default: {cfg.merge.envDefault || "(none)"}
              </span>
            </div>
          </div>
          <label className="mt-4 flex cursor-pointer items-start gap-2.5">
            <input
              type="checkbox"
              checked={autoMergeDraft}
              onChange={(e) => setAutoMergeDraft(e.target.checked)}
              className="mt-0.5 h-4 w-4 cursor-pointer"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-800">
                Auto-run AI merge for duplicates
              </span>
              <span className="block text-xs text-gray-500">
                When the review page loads, immediately compare each duplicate
                against its existing record so the merged result is ready to
                review. Turn off to run merges manually.
              </span>
            </span>
          </label>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={saveSettings}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
            >
              Save
            </button>
            <SecondaryButton
              type="button"
              onClick={() => setSettingsOpen(false)}
            >
              Cancel
            </SecondaryButton>
          </div>
        </div>
      )}

      {missingChains && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          One or more chain IDs are not configured.{" "}
          {!ingestChainId && <span>Set the ingest chain ID. </span>}
          {!mergeChainId && <span>Set the merge chain ID. </span>}
          Open <span className="font-semibold">⚙ Settings</span> to add them.
        </div>
      )}

      {/* Mode switch: the guided "add" flow vs. maintaining existing items. */}
      <div className="mt-5 flex flex-wrap gap-2">
        <ModeButton active={mode === "add"} onClick={() => setMode("add")}>
          ➕ Add new products
        </ModeButton>
        <ModeButton
          active={mode === "manage"}
          onClick={() => setMode("manage")}
        >
          🔎 Search &amp; edit existing
        </ModeButton>
        <ModeButton active={mode === "mine"} onClick={() => setMode("mine")}>
          📂 Your products
        </ModeButton>
      </div>

      {mode === "add" ? (
        <div className="mt-5">
          {/* Plain-English overview so anyone knows what happens. */}
          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-2.5 text-xs text-gray-600">
            <span className="font-semibold text-gray-700">How it works:</span>{" "}
            add your products → we research &amp; fact-check them online →
            review and tweak anything → write the good ones to the catalog.
            Nothing is saved until you click write.
          </div>

          {/* Step rail */}
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-stretch">
            <StepPill
              index={1}
              label="Add products"
              hint="Names, UPCs, or links"
              state={step === "input" ? "active" : "done"}
              enabled
              onClick={() => setStep("input")}
            />
            <StepPill
              index={2}
              label="Review & write"
              hint={
                hasReview
                  ? `${reviewItems.length} ready to review`
                  : "Run step 1 first"
              }
              state={step === "review" ? "active" : hasReview ? "done" : "todo"}
              enabled={hasReview}
              onClick={() => hasReview && setStep("review")}
            />
          </div>

          <div className="mt-6">
            <div className={step === "input" ? "block" : "hidden"}>
              <IngestTab
                ingestChainId={ingestChainId}
                onSubmit={submitIngest}
                activeCount={jobs.activeCount}
              />
            </div>
            <div className={step === "review" ? "block" : "hidden"}>
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  Review &amp; write to catalog
                </h3>
                <SecondaryButton type="button" onClick={startNewBatch}>
                  ← Add more products
                </SecondaryButton>
              </div>
              <ReviewTab
                api={api}
                mergeChainId={mergeChainId}
                reviewItems={reviewItems}
                executionId={reviewExecutionId}
                customerName={customerName}
                autoMerge={autoMerge}
              />
            </div>
          </div>
        </div>
      ) : mode === "manage" ? (
        <div className="mt-6">
          <SearchEditTab
            api={api}
            ingestChainId={ingestChainId}
            customerName={customerName}
            onSubmit={submitIngest}
            activeCount={jobs.activeCount}
          />
        </div>
      ) : (
        <div className="mt-6">
          <ContributionsTab
            api={api}
            ingestChainId={ingestChainId}
            customerName={customerName}
          />
        </div>
      )}

      {/* Live progress dialog for the focused background run (hideable). */}
      <ChainRunOverlay
        open={focusedInFlight}
        progress={focusedJob?.progress || null}
        credits={focusedJob?.credits}
        title={focusedJob ? `Researching: ${focusedJob.label}` : undefined}
        onHide={() => setFocusedJobId(null)}
        onCancel={() => {
          if (focusedJobId) jobs.cancel(focusedJobId);
          setFocusedJobId(null);
        }}
      />

      {/* Persistent tray of active + recent runs (the "alert area"). */}
      <IngestJobsTray
        jobs={jobs.jobs}
        activeCount={jobs.activeCount}
        onOpen={setFocusedJobId}
        onView={openRunResults}
        onCancel={jobs.cancel}
        onClear={jobs.clear}
        onClearFinished={jobs.clearFinished}
      />
    </div>
  );
};

export default CatalogManager;
